import { toast } from "sonner";
import { useGlobalContext } from "../GlobalContext";

export default function useGigs() {
  const { api, AdminId } = useGlobalContext();

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  // ---------- GET SLOTS ----------

  const fetchSlotsApi = async (
    date,
    setSlotsData,
    setFilteredData,
    setLoading
  ) => {
    setLoading(true);

    try {
      const res = await api.post("/api/Driver/v2.0/getSlotsList", {
        date,
        adminId: AdminId,
      });

      if (res.data?.responseCode === "00") {
        const [year, month, day] = date.split("-");

        const months = [
          "JAN",
          "FEB",
          "MAR",
          "APR",
          "MAY",
          "JUN",
          "JUL",
          "AUG",
          "SEP",
          "OCT",
          "NOV",
          "DEC",
        ];

        const jsDate = new Date(year, month - 1, day);

        const dayName = jsDate
          .toLocaleDateString("en-US", { weekday: "long" })
          .toUpperCase();

        const formattedDate = `${day} ${months[month - 1]}, ${dayName}`;

        const filtered = (res.data?.data || []).filter(
          (slot) => slot?.slot_date?.toUpperCase() === formattedDate
        );

        setSlotsData(filtered);
        setFilteredData(filtered);

        return filtered;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to fetch slots");
      }

      setSlotsData([]);
      setFilteredData([]);
      return [];
    } catch (error) {
      console.error("fetchSlotsApi error:", error);
      toast.error("Error fetching slots");
      setSlotsData([]);
      setFilteredData([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // ---------- DELETE SLOT ----------
  const deleteSlotApi = async (slotId, onSuccess) => {
    if (!slotId) {
      toast.error("Invalid slot ID");
      return false;
    }

    try {
      const res = await api.post("/api/Driver/v1.0/deleteSlot", {
        slotId,
        adminId: AdminId,
      });

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Slot deleted successfully");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to delete slot");
      }

      return false;
    } catch (error) {
      console.error("deleteSlotApi error:", error);
      toast.error("Error deleting slot");
      return false;
    }
  };

  // ---------- GET BOOKED DRIVERS ----------

  const fetchBookedDriversApi = async (
    slotId,
    setDriverList,
    setDriverListLoading
  ) => {
    setDriverListLoading(true);

    try {
      const res = await api.post("/api/Driver/v1.0/getSlotBookedDriverList", {
        slotId,
        adminId: AdminId,
      });

      if (res.data?.responseCode === "00") {
        const list = res.data?.data || [];
        setDriverList(list);
        return list;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to fetch drivers");
      }

      setDriverList([]);
      return [];
    } catch (error) {
      console.error("fetchBookedDriversApi error:", error);
      toast.error("Error fetching driver list");
      setDriverList([]);
      return [];
    } finally {
      setDriverListLoading(false);
    }
  };

// ---------- CREATE SLOT ----------

const createSlotApi = async (form, setLoading, onSuccess) => {
  if (!form) {
    toast.error("Invalid slot data");
    return false;
  }

  const payload = {
    adminId: String(AdminId || ""),
    gigDate: String(form.gigDate || ""),
    gigTitle: String(form.gigTitle || ""),
    gigDescription: String(form.gigDescription || ""),
    fromTime: String(form.fromTime || ""),
    toTime: String(form.toTime || ""),
    minAmount: String(form.minAmount || ""),
    maxAmount: String(form.maxAmount || ""),
    category: String(form.category || ""),
  };

  try {
    setLoading?.(true);

    const res = await api.post("/api/Driver/v1.0/createSlot", payload);

    if (res.data?.responseCode === "00") {
      toast.success(res.data?.responseMessage || "Slot created successfully");

      if (typeof onSuccess === "function") {
        await onSuccess();
      }

      return true;
    }

    if (shouldShowLocalError(res.data)) {
      toast.error(res.data?.responseMessage || "Failed to create slot");
    }

    return false;
  } catch (error) {
    console.error("createSlotApi error:", error);
    toast.error(
      error?.response?.data?.responseMessage || "Error creating slot"
    );
    return false;
  } finally {
    setLoading?.(false);
  }
};

return {
  fetchSlotsApi,
  createSlotApi,
  deleteSlotApi,
  fetchBookedDriversApi,
};
}